import useBlobToImageURL from "@/hooks/useBlobToImageURL";
import { AttachmentMeta } from "@/indexedDB/types";
import { processMediaAttachment } from "@/middleware/attachments";
import {
  getCachedMedia,
  mediaCacheKey,
  peekCachedMedia,
} from "@/middleware/mediaMemoryCache";
import { useState, useEffect, useRef } from "react";

/**
 * Options for the useMediaAttachment hook
 */
type UseMediaAttachmentOptions = {
  messageId: string;
  messageTimestamp: string;
  chatId: string;
  attachment: AttachmentMeta;
  getThumbnail?: boolean;
  canExecute?: boolean;
  /** Bump to force a re-fetch of the same attachment (retry after a failure). */
  reloadKey?: number;
};

/**
 * Hook for loading media attachments from messages
 *
 * This hook handles the asynchronous loading of media attachments (images and videos)
 * from encrypted messages. It manages the loading state and returns the attachment URL
 * when available.
 *
 * @param options - The options for loading the attachment
 * @returns Object containing loading state and attachment URL
 */
const useMediaAttachment = ({
  messageId,
  messageTimestamp,
  chatId,
  attachment,
  getThumbnail = true,
  canExecute = true,
  reloadKey = 0,
}: UseMediaAttachmentOptions) => {
  // Synchronous cache probe: media already decrypted this session is available
  // on the FIRST render, so a re-open or a swipe back paints the real thing
  // instead of the blur/poster placeholder. Miss → null, and the effect below
  // fetches as before.
  const cacheKey = mediaCacheKey(
    attachment.attachmentId,
    getThumbnail ? "thumbnail" : "content",
  );
  // peek (not get): render must stay side-effect-free, so don't reorder the
  // LRU here — the effect below does the recency touch after commit.
  const cachedBlob = peekCachedMedia(cacheKey);

  const [loading, setLoading] = useState<boolean>(!cachedBlob);
  const [attachmentBlob, setAttachmentBlob] = useState<Blob | null>(
    cachedBlob ?? null,
  );

  // Apply the probe on IDENTITY CHANGES too, not just on mount. useState
  // initializers run once per mounted instance — but the viewer's dialog is
  // mounted persistently (one hook instance for every item it ever shows), so
  // without this the hit was computed and thrown away, and every open/swipe
  // committed a first render with loading=true even for fully cached media
  // (which for videos flipped the poster branch and remounted the <video>).
  // This is React's canonical "adjust state when props change" pattern: setState
  // during render makes React re-render immediately BEFORE committing, so the
  // committed first frame of a new item already has the right state. A miss
  // blanks the previous item's blob in the same way — which also stops the old
  // item's pixels from being committed once inside the new item's frame.
  const [renderedKey, setRenderedKey] = useState(cacheKey);
  if (renderedKey !== cacheKey) {
    setRenderedKey(cacheKey);
    setAttachmentBlob(cachedBlob ?? null);
    setLoading(!cachedBlob);
  }

  const attachmentUrl = useBlobToImageURL(attachmentBlob);

  // Keep the latest per-message values reachable from the effect WITHOUT making
  // them effect dependencies. A message's Dexie row is updated IN PLACE on every
  // status transition (PENDING → SENT → DELIVERED/READ) and the live query
  // re-emits a NEW `attachment` object + `messageId` each time, even though the
  // media is byte-identical. Depending on those by reference re-ran the fetch
  // and blanked the <img> on every transition — the send-time flicker.
  const latest = useRef({ chatId, messageId, messageTimestamp, attachment });
  latest.current = { chatId, messageId, messageTimestamp, attachment };

  // The STABLE identity that actually determines which blob to load. Keying the
  // effect on these (not the object ref) means it runs once per real attachment
  // — and on an explicit reloadKey retry — not on every status re-render.
  const { attachmentId, fileType } = attachment;

  /**
   * Effect to load the attachment. Runs on a genuine change of the attachment
   * IDENTITY (attachmentId/fileType), the thumbnail-vs-full choice, gating, or a
   * reloadKey retry — NOT on message status re-renders.
   */
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    // Guards state updates: once this effect is superseded (identity changed) or
    // the component unmounts, a still-in-flight fetch must not publish a stale
    // blob/loading value over the newer effect's.
    let active = true;
    // A memory-cache hit is already rendered (see the probe above) — blanking
    // it here would reintroduce the very placeholder flash we're removing.
    const hit = getCachedMedia(cacheKey);
    if (hit) {
      setAttachmentBlob(hit);
      setLoading(false);
    } else {
      setLoading(true);
      setAttachmentBlob(null);
    }

    /**
     * Asynchronously loads the attachment and updates component state.
     */
    const attachmentInit = async () => {
      // Read the latest per-message values (deliberately NOT effect deps — see
      // above); the fetch is keyed on the attachment id, these only tag a
      // newly-created cache row.
      const {
        chatId: cId,
        messageId: mId,
        messageTimestamp: ts,
        attachment: meta,
      } = latest.current;
      // processMediaAttachment resolves null (never throws) on abort/failure.
      const _attachmentBlob = await processMediaAttachment({
        chatId: cId,
        messageId: mId,
        messageTimestamp: ts,
        attachmentMeta: meta,
        getThumbnail,
        signal,
      });
      if (!active) {
        return;
      }
      setAttachmentBlob(_attachmentBlob);
      setLoading(false);
    };

    if (
      (fileType?.startsWith("image/") || fileType?.startsWith("video/")) &&
      canExecute
    ) {
      attachmentInit();
    }

    return () => {
      active = false;
      // Cancel the request when the component unmounts / the identity changes.
      controller.abort();
    };
    // cacheKey is derived from attachmentId + getThumbnail, so it changes
    // exactly when they do — listed to satisfy exhaustive-deps.
  }, [attachmentId, fileType, getThumbnail, canExecute, reloadKey, cacheKey]);

  return { loading, attachmentUrl };
};

export default useMediaAttachment;
